import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiBriefcase, FiCalendar, FiMapPin, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const experiences = [
  {
    company: 'Appstrail Technology',
    role: 'Associate Consultant Trainee',
    period: 'October 2025 — Present',
    location: 'Bengaluru, Karnataka, India',
    type: 'Current',
    color: 'primary',
    points: [
      'Mobile App Developer specializing in React Native, building scalable, offline-first, and performant cross-platform applications.',
      'Developed and deployed Salesforce automation applications (SFA Apps) with robust offline capabilities to streamline enterprise business workflows.',
      'Engineered reusable Salesforce SDKs for seamless mobile integration, optimizing system-level tasks and data synchronization.',
    ],
  },
  {
    company: 'Scontinent Technologies Pvt Ltd',
    role: 'Software Development Engineer',
    period: 'June 2024 — October 2025',
    location: 'Bengaluru, Karnataka, India',
    type: 'Full-time',
    color: 'accent',
    points: [
      'Developed and maintained scalable EdTech software applications using React.js, Node.js, Firebase, and RESTful Web APIs, serving thousands of students across Karnataka.',
      'Architected and integrated AI/ML-driven features including GenAI-powered platforms for intelligent user interactions and predictive analytics.',
      'Designed backend REST API services with Python (Flask/Django), enabling seamless third-party Web API integrations.',
      'Automated deployment and operational tasks using Shell scripting (Bash) within Linux environments, improving system reliability and CI/CD workflows.',
      'Led full-stack development of NammaCET platform using machine learning algorithms for KCET rank prediction and college allocation for 10,000+ Karnataka students.',
    ],
  },
  {
    company: 'Aitch Nu Global Technologies Pvt Ltd',
    role: 'Lead & Software Developer Executive',
    period: 'May 2024 — April 2025',
    location: 'Bengaluru, Karnataka, India',
    type: 'Full-time',
    color: 'blue',
    points: [
      'Led a team of developers delivering 10+ successful software projects using Java, MERN Stack, PHP, and Python with advanced AI/ML model integration.',
      'Designed and implemented Web API integrations, microservices architectures, and MVC patterns for enterprise-level client applications.',
      'Developed desktop and mobile applications (Android Studio, React Native) for various industry clients, including healthcare and e-commerce sectors.',
      'Conducted systematic code reviews, enforced coding standards, and mentored junior developers to maintain high-quality software delivery.',
    ],
  },
  {
    company: 'Indian Council of Medical Research (ICMR – NCDIR)',
    role: 'Research & Development Intern',
    period: 'February 2024 — April 2024',
    location: 'Bengaluru, Karnataka, India',
    type: 'Internship',
    color: 'violet',
    points: [
      'Contributed to R&D software initiatives, developing data-driven solutions and analytical tools using Python for medical research datasets.',
      'Assisted in designing automated data pipelines and applying machine learning techniques for research insights.',
    ],
  },
];

const colorConfig = {
  primary: {
    dot: 'bg-primary-600',
    line: 'border-primary-200',
    badge: 'bg-primary-50 text-primary-700 border-primary-200',
    icon: 'bg-primary-50 text-primary-600',
    accent: 'border-l-primary-500',
  },
  accent: {
    dot: 'bg-accent-600',
    line: 'border-accent-200',
    badge: 'bg-accent-50 text-accent-700 border-accent-200',
    icon: 'bg-accent-50 text-accent-600',
    accent: 'border-l-accent-500',
  },
  blue: {
    dot: 'bg-blue-600',
    line: 'border-blue-200',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'bg-blue-50 text-blue-600',
    accent: 'border-l-blue-500',
  },
  violet: {
    dot: 'bg-violet-600',
    line: 'border-violet-200',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    icon: 'bg-violet-50 text-violet-600',
    accent: 'border-l-violet-500',
  },
};

function ExperienceCard({ exp, index, isLast }) {
  const [expanded, setExpanded] = useState(index === 0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const cfg = colorConfig[exp.color];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative flex gap-6"
    >
      {/* Timeline */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-4 h-4 rounded-full ${cfg.dot} border-4 border-white shadow-md mt-1 z-10`} />
        {!isLast && <div className="w-0.5 bg-slate-200 flex-1 mt-1" />}
      </div>

      {/* Card */}
      <div className="flex-1 pb-10">
        <div
          className={`card border-l-4 ${cfg.accent} overflow-hidden cursor-pointer`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-display font-bold text-slate-900 text-base">{exp.company}</h3>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${cfg.badge}`}>
                    {exp.type}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600">{exp.role}</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600 transition-colors mt-1">
                {expanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </button>
            </div>

            <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <FiCalendar size={12} className="flex-shrink-0" />
                {exp.period}
              </span>
              <span className="flex items-center gap-1.5">
                <FiMapPin size={12} className="flex-shrink-0" />
                {exp.location}
              </span>
            </div>

            {/* Expandable Points */}
            <motion.div
              initial={false}
              animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <ul className="mt-4 space-y-2.5 border-t border-slate-100 pt-4">
                {exp.points.map((pt, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-600">
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} flex-shrink-0 mt-1.5`} />
                    {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Experience() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="experience" className="bg-white">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Experience</p>
          </div>
          <h2 className="section-title">Professional Journey</h2>
          <p className="text-slate-400 text-sm">A track record of delivering real software to real users.</p>
        </motion.div>

        <div>
          {experiences.map((exp, i) => (
            <ExperienceCard key={i} exp={exp} index={i} isLast={i === experiences.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
